import { Box, Button, Container, Heading, Text, VStack, useToast, Flex, Alert, AlertIcon, Spinner, Collapse, useDisclosure, Icon } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import YouTube from 'react-youtube';
import { supabase } from '../../lib/supabase';
import { useState } from 'react';
import ErrorBoundary from '../../components/ErrorBoundary';
import { useCourse } from '../../contexts/CourseContext';
import { useCourseConfig } from '../../hooks/useCourseConfig';

// Custom error fallback for course page
const CourseErrorFallback = ({ error, resetErrorBoundary }: { error: Error | null, resetErrorBoundary: () => void }) => {
  const navigate = useNavigate();
  const [isReloading, setIsReloading] = useState(false);

  const handleReset = () => {
    setIsReloading(true);
    resetErrorBoundary();
    window.location.reload();
  };

  return (
    <Container maxW="container.lg" py={8}>
      <Alert status="error" borderRadius="md" mb={6}>
        <AlertIcon />
        <Box flex="1">
          <Heading size="md" mb={2}>Course Loading Error</Heading>
          <Text>We're having trouble loading the course content. Please try refreshing the page or contact support if the issue persists.</Text>
          {process.env.NODE_ENV === 'development' && error && (
            <Box mt={2} p={2} bg="blackAlpha.100" borderRadius="md" fontSize="sm">
              <Text fontWeight="bold">Error details:</Text>
              <Text as="pre" whiteSpace="pre-wrap" fontFamily="mono">
                {error.message}
              </Text>
            </Box>
          )}
        </Box>
      </Alert>
      
      <VStack spacing={4}>
        <Button
          colorScheme="blue"
          onClick={handleReset}
          isLoading={isReloading}
          loadingText="Reloading..."
          leftIcon={<Box as="span" className="material-icons">refresh</Box>}
        >
          Reload Page
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          isDisabled={isReloading}
        >
          Return to Home
        </Button>
      </VStack>
    </Container>
  );
};

// Main CourseLandingPage component
function CourseLandingPageContent() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentCourse } = useCourse();
  const { landing } = useCourseConfig();
  const { isOpen: isIntroOpen, onToggle: onIntroToggle } = useDisclosure();
  const toast = useToast();

  const handleConsent = async () => {
    setIsSubmitting(true);
    try {
      // Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Upsert the consent in the course_learner_preferences table
      if (!currentCourse) {
        throw new Error('No course assigned to user');
      }

      const { error } = await supabase
        .from('course_learner_preferences')
        .upsert(
          { 
            user_id: user.id, 
            course_id: currentCourse.id,
            consent_given: true,
            // consent_timestamp may be set by DB default; set explicitly for clarity
            consent_timestamp: new Date().toISOString()
          },
          { 
            onConflict: 'user_id,course_id',
            ignoreDuplicates: false
          }
        );

      if (error) throw error;

      // Show success message and navigate
      toast({
        title: 'Consent recorded',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      navigate('/preferences');
    } catch (error) {
      console.error('Error recording consent:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      toast({
        title: 'Error recording consent',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box bg="white" minH="100vh" py={12}>
      <Container maxW="4xl">
        {/* Header Section */}
        <VStack spacing={6} textAlign="center" mb={6}>
          <Heading as="h1" size="2xl" color="gray.800">
            {landing?.title ?? 'Rad Learning Tutor'}
          </Heading>
          <Text fontSize="xl" color="gray.600" maxW="2xl">
            {landing?.subtitle ?? 'Welcome to the first part of the Neurodiversity Learning Experience!'}
          </Text>
        </VStack>


        {/* Demonstration Steps */}
        <Box 
          bg="white" 
          p={8} 
          borderRadius="xl" 
          mb={12}
          borderWidth="2px" 
          borderColor="#1AC8ED"
          boxShadow="sm"
        >
            <Heading as="h3" size="lg" color="#C200FB" mb={6}>
              How do I complete this learning experience?
            </Heading>
            <Box as="ol" listStyleType="none">
              {[
                <Text key="watch"><Text as="span" fontWeight="bold">Watch</Text> the Rad Learning Explainer video.</Text>,
                <Text key="consent"><Text as="span" fontWeight="bold">Consent</Text> to having your interactions with the Tutor recorded.</Text>,
                <Text key="preferences"><Text as="span" fontWeight="bold">Set</Text> your communication preferences.</Text>,
                <Text key="activity"><Text as="span" fontWeight="bold">Answer</Text> a quick question about your learning goals.</Text>,
                <Text key="chat">Have a <Text as="span" fontWeight="bold">chat</Text> with the AI Tutor about what you learned from the video.</Text>,
              ].map((item, index) => (
                <Flex as="li" key={index} mb={2}>
                  <Flex align="flex-start">
                    <Flex
                      align="center"
                      justify="center"
                      bg="#C200FB"
                      color="white"
                      fontWeight="bold"
                      borderRadius="full"
                      w={8}
                      h={8}
                      flexShrink={0}
                      mr={3}
                      aria-hidden="true"
                    >
                      {index + 1}
                    </Flex>
                    <Box flex="1" mt={1} lineHeight="tall">{item}</Box>
                  </Flex>
                </Flex>
              ))}
            </Box>
          </Box>

        {/* Collapsible Introduction Section - Moved below activity steps */}
        <Box mb={12}>
          <Box 
            bg="white" 
            p={8} 
            borderRadius="xl" 
            borderWidth="2px"
            borderColor="#C200FB"
            boxShadow="sm"
          >
            <Button
              onClick={onIntroToggle}
              variant="ghost"
              width="100%"
              p={4}
              height="auto"
              borderRadius="md"
              _hover={{ bg: 'purple.50' }}
              _active={{ bg: 'purple.100' }}
              aria-expanded={isIntroOpen}
              aria-controls="introduction-content"
              transition="all 0.2s"
              display="block"
              textAlign="left"
            >
              <Heading as="h2" size="lg" color="#C200FB" mb={3}>
                Want to know more about the Rad Learning Tutor?
              </Heading>
              <Flex align="center" gap={2}>
                <Box
                  bg="#C200FB"
                  color="white"
                  borderRadius="md"
                  p={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon 
                    as={isIntroOpen ? ChevronUpIcon : ChevronDownIcon} 
                    w={5} 
                    h={5}
                    aria-hidden="true"
                  />
                </Box>
                <Text fontSize="sm" color="gray.500" fontWeight="normal">
                  {isIntroOpen ? 'Click to hide' : 'Click to expand'}
                </Text>
              </Flex>
            </Button>
            
            <Collapse in={isIntroOpen} animateOpacity>
              <Box id="introduction-content" mt={6} role="region" aria-label="Additional information about the Rad Learning Tutor">
                <Heading as="h3" size="md" color="gray.800" mb={3}>
                  What is it?
                </Heading>
                <Text color="gray.700" lineHeight="tall" mb={6}>
                  The Rad Learning Tutor is an AI-powered chat application that helps you to embed your learning by asking questions. This will give you an opportunity to explore what you know and what you're still learning. Even though it has access to all the information in your learning materials, it's not a question-answering chatbot - <Text as="span" fontWeight="bold">it's a question-asking chatbot</Text>.
                </Text>

                <Heading as="h3" size="md" color="gray.800" mb={3} mt={8}>
                  How does it work?
                </Heading>
                <Text color="gray.700" lineHeight="tall" mb={6}>
                  Rad Learning has given the chatbot the information that you are in the process of learning. The tutor uses generative AI technology to <Text as="span" fontWeight="bold">create questions and respond to your comments</Text> in line with the information. However, generative AI can sometimes be mistaken, so it's always important to ask a Rad Learning Educator if there's anything that is concerning or doesn't make sense.
                </Text>

                <Heading as="h3" size="md" color="gray.800" mb={3} mt={8}>
                  Why is it important?
                </Heading>
                <Text color="gray.700" lineHeight="tall">
                  The information from your conversation with the Rad Learning Tutor is an essential part of the Rad Learning Model. An educator will use this information to tailor the learning experience to the cohort. Your responses can let the educator know <Text as="span" fontWeight="bold">what participants know already, what needs more work, and what else you might find relevant</Text>.
                </Text>
              </Box>
            </Collapse>
          </Box>
        </Box>

        {/* Video Section */}
        <Box 
          bg="white"
          p={8}
          borderRadius="xl"
          borderWidth="2px"
          borderColor="#1AC8ED"
          boxShadow="sm"
          mb={12}
        >
          <Heading as="h2" size="lg" color="#C200FB" mb={6} textAlign="center">
            Step 1: Watch the Rad Learning Explainer Video
          </Heading>
          <Box 
            bg="gray.50"
            borderRadius="lg" 
            p={{ base: 4, md: 8 }} 
            display="flex" 
            justifyContent="center" 
            alignItems="center"
            mt={4}
            width="100%"
            overflow="hidden"
          >
            <Box 
              width="100%" 
              maxW="800px"
              position="relative"
              paddingBottom="56.25%" /* 16:9 Aspect Ratio */
              height="0"
              overflow="hidden"
            >
              <Box 
                as="div"
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                borderRadius="lg"
                overflow="hidden"
                boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
              >
                <YouTube
                  videoId={landing?.videoId ?? 'g_16IsJRCOE'}
                  opts={{
                    width: '100%',
                    height: '100%',
                    playerVars: {
                      autoplay: 0,
                      controls: 1,
                      rel: 0,
                      showinfo: 0,
                      modestbranding: 1
                    },
                  }}
                  style={{
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Consent Section */}
        <Box 
          bg="white" 
          p={8} 
          borderRadius="xl" 
          borderWidth="2px"
          borderColor="#C200FB"
          boxShadow="sm"
          mb={12}
        >
          <Heading as="h2" size="lg" color="#C200FB" mb={6}>
            Step 2: Consent to having your interactions recorded
          </Heading>
          <Heading as="h3" size="md" color="gray.800" mb={4}>
            Our tutor's a great listener!
          </Heading>
          <VStack align="start" width="100%" mb={8} spacing={4} px={0}>
            <Text color="gray.700" width="100%" textAlign="left" px={0}>
              By chatting, you agree that Rad Learning can record and review the conversation that you have with the Tutor.
            </Text>
            <Text color="gray.700" fontWeight="bold" width="100%" textAlign="left" px={0}>
              We only use your conversation to improve the Rad Learning Experience, we will never share it with anyone else.
            </Text>
          </VStack>
          <Button
            colorScheme="blue"
            size="lg"
            onClick={handleConsent}
            isLoading={isSubmitting}
            loadingText="Processing..."
            rightIcon={<span>→</span>}
          >
            I consent to recording this conversation
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

// Export the wrapped component
export default function CourseLandingPage() {
  const { currentCourse, isLoading } = useCourse();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (!currentCourse) {
    return (
      <Box textAlign="center" py={10} px={6}>
        <Heading as="h2" size="xl" mt={6} mb={2}>
          No Course Assigned
        </Heading>
        <Text color={'gray.500'} mb={6}>
          You haven't been assigned to a course yet. Please contact your administrator.
        </Text>
        <Button
          colorScheme="purple"
          onClick={() => window.location.href = '/'}
        >
          Return to Home
        </Button>
      </Box>
    );
  }

  return (
    <ErrorBoundary 
      fallback={
        <CourseErrorFallback 
          error={null} 
          resetErrorBoundary={() => window.location.reload()} 
        />
      }
    >
      <CourseLandingPageContent />
    </ErrorBoundary>
  );
}
